import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Download, ArrowRight, Share2, Instagram, Copy, Mail, FileMusic, Shield, Zap } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { siteConfig, contactInfo } from "../data/mock";

/* ═══════════════ CONFETTI EFFECT ═══════════════ */
const Confetti = () => {
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    const colors = ['#DAFF01', '#FFFFFF', '#4ADE80', '#A3E635'];
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 2 + Math.random() * 2,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 4 + Math.random() * 8,
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          initial={{ y: -20, x: `${p.x}vw`, opacity: 1, rotate: 0 }}
          animate={{ y: '100vh', opacity: 0, rotate: 360 }}
          transition={{ duration: p.duration, delay: p.delay, ease: 'linear' }}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.size > 8 ? '2px' : '50%',
          }}
        />
      ))}
    </div>
  );
};

/* ═══════════════ BADGE COMPONENT ═══════════════ */
const CertifiedBadge = () => (
  <div className="relative">
    {/* Badge Container */}
    <div className="bg-[rgb(17,17,19)] border-2 border-[rgb(218,255,1)] rounded-2xl p-8 text-center max-w-sm mx-auto">
      {/* Glow effect */}
      <div className="absolute inset-0 bg-[rgba(218,255,1,0.05)] rounded-2xl blur-xl" />
      
      <div className="relative z-10">
        {/* Shield Icon */}
        <div className="w-20 h-20 mx-auto mb-4 bg-[rgb(218,255,1)] rounded-full flex items-center justify-center">
          <Shield size={40} className="text-[rgb(17,17,19)]" />
        </div>
        
        {/* Badge Text */}
        <h3 
          className="text-[rgb(218,255,1)] text-xl font-bold tracking-[0.15em] mb-2"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          ONE-STOP CERTIFIED
        </h3>
        <p 
          className="text-white text-sm font-medium mb-1"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          ITZGREEN MUSIC
        </p>
        <p 
          className="text-[rgb(161,161,170)] text-xs"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Global Rights Administration
        </p>
        
        {/* Decorative line */}
        <div className="w-16 h-[2px] bg-[rgb(218,255,1)] mx-auto mt-4" />
        
        <p 
          className="text-[rgb(161,161,170)] text-[10px] tracking-[0.2em] mt-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          100% HUMAN-COMPOSED • PRE-CLEARED
        </p>
      </div>
    </div>
  </div>
);

/* ═══════════════ MISSION CARD ═══════════════ */
const MissionCard = ({ number, title, description, action, actionLink, icon: Icon, isPrimary }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.3 + number * 0.15 }}
    className={`bg-[rgb(26,28,30)] border ${isPrimary ? 'border-[rgb(218,255,1)]' : 'border-[rgba(255,255,255,0.1)]'} rounded-xl p-6 relative overflow-hidden`}
  >
    {isPrimary && <div className="absolute top-0 left-0 right-0 h-[2px] bg-[rgb(218,255,1)]" />}
    
    <div className="flex items-start gap-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isPrimary ? 'bg-[rgb(218,255,1)] text-[rgb(17,17,19)]' : 'bg-[rgba(218,255,1,0.1)] text-[rgb(218,255,1)]'}`}>
        <Icon size={20} />
      </div>
      
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span 
            className="text-[rgb(218,255,1)] text-[10px] tracking-[0.2em]"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            MISSION {number}
          </span>
          {isPrimary && (
            <Badge className="bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-[8px] px-2 py-0">
              PRIORITY
            </Badge>
          )}
        </div>
        
        <h4 
          className="text-white text-lg font-bold mb-2"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          {title}
        </h4>
        
        <p 
          className="text-[rgb(161,161,170)] text-sm mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {description}
        </p>
        
        <a
          href={actionLink}
          target={actionLink.startsWith('mailto') ? undefined : '_blank'}
          rel={actionLink.startsWith('mailto') ? undefined : 'noopener noreferrer'}
          className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.1em] px-4 py-2 rounded-lg transition-all duration-300 ${
            isPrimary 
              ? 'bg-[rgb(218,255,1)] text-[rgb(17,17,19)] hover:bg-[rgb(166,190,21)]' 
              : 'border border-[rgb(63,63,63)] text-white hover:border-[rgb(218,255,1)] hover:text-[rgb(218,255,1)]'
          }`}
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {action}
          <ArrowRight size={14} />
        </a>
      </div>
    </div>
  </motion.div>
);

/* ═══════════════ WELCOME PAGE ═══════════════ */
const WelcomePage = () => {
  const [searchParams] = useSearchParams();
  const [showConfetti, setShowConfetti] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Get producer name from URL query params (e.g., ?name=D-Workz)
  const producerName = searchParams.get('name') || searchParams.get('signer_name') || null;

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  const handleCopyBadge = () => {
    // In production, this would copy a shareable link or badge image
    navigator.clipboard.writeText('https://sync-licensing-hub.preview.emergentagent.com/welcome');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadBadge = () => {
    // Link to the SVG badge in public assets
    const link = document.createElement('a');
    link.href = '/assets/Certified_One_Stop_Badge.svg';
    link.download = 'ItzGreen_Certified_Badge.svg';
    link.click();
  };

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] relative overflow-hidden">
      {/* Confetti */}
      {showConfetti && <Confetti />}
      
      {/* Background grid */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      
      {/* Glow orbs */}
      <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-[rgba(218,255,1,0.05)] rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-[rgba(218,255,1,0.03)] rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-4xl mx-auto px-6 py-20 relative z-10">
        
        {/* ═══ STATUS HEADER ═══ */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          {/* Status Badge */}
          <div className="inline-flex items-center gap-2 bg-[rgba(218,255,1,0.1)] border border-[rgb(218,255,1)] rounded-full px-4 py-2 mb-6">
            <CheckCircle size={16} className="text-[rgb(218,255,1)]" />
            <span 
              className="text-[rgb(218,255,1)] text-xs font-semibold tracking-[0.15em]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              CONTRACT EXECUTED
            </span>
          </div>
          
          {/* Main Headline */}
          <h1 
            className="text-white text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
            style={{ fontFamily: "'Oswald', sans-serif" }}
            data-testid="welcome-headline"
          >
            ACCESS <span className="text-[rgb(218,255,1)]">GRANTED.</span>
          </h1>
          
          {/* Personalized Welcome Message */}
          {producerName ? (
            <p 
              className="text-[rgb(218,218,218)] text-lg md:text-xl max-w-2xl mx-auto mb-2"
              style={{ fontFamily: "'Inter', sans-serif" }}
              data-testid="welcome-personalized"
            >
              Welcome to the Alliance, <span className="text-[rgb(218,255,1)] font-bold">{producerName}</span>.
            </p>
          ) : (
            <p 
              className="text-[rgb(218,218,218)] text-lg md:text-xl max-w-2xl mx-auto mb-2"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              You are now an Official Roster Member of ItzGreen Music.
            </p>
          )}
          
          <p 
            className="text-[rgb(161,161,170)] text-sm"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {producerName 
              ? "You are now an Official Roster Member. Your catalog is pre-cleared for global sync licensing."
              : "Welcome to the Alliance. Your catalog is now pre-cleared for global sync licensing."
            }
          </p>
        </motion.div>

        {/* ═══ BADGE SECTION ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-16"
        >
          <div className="text-center mb-6">
            <span 
              className="text-[rgb(218,255,1)] text-[10px] tracking-[0.35em] font-medium"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              YOUR CREDENTIAL
            </span>
          </div>
          
          <CertifiedBadge />
          
          {/* Badge Actions */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
            <Button
              onClick={handleDownloadBadge}
              className="bg-[rgb(218,255,1)] text-[rgb(17,17,19)] font-semibold text-xs tracking-[0.1em] px-6 py-3 rounded-xl hover:bg-[rgb(166,190,21)] transition-all"
              style={{ fontFamily: "'Inter', sans-serif" }}
              data-testid="download-badge-btn"
            >
              <Download size={14} className="mr-2" />
              DOWNLOAD BADGE
            </Button>
            
            <Button
              onClick={handleCopyBadge}
              variant="outline"
              className="border-[rgb(63,63,63)] text-white font-semibold text-xs tracking-[0.1em] px-6 py-3 rounded-xl hover:border-[rgb(218,255,1)] hover:text-[rgb(218,255,1)] transition-all"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {copied ? (
                <>
                  <CheckCircle size={14} className="mr-2" />
                  COPIED!
                </>
              ) : (
                <>
                  <Share2 size={14} className="mr-2" />
                  SHARE PROFILE
                </>
              )}
            </Button>
          </div>
          
          <p 
            className="text-center text-[rgb(161,161,170)] text-xs mt-4"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Post this badge on your Instagram bio to flex your certification.
          </p>
        </motion.div>

        {/* ═══ MISSION BRIEFING ═══ */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mb-16"
        >
          <div className="text-center mb-8">
            <span 
              className="text-[rgb(218,255,1)] text-[10px] tracking-[0.35em] font-medium block mb-2"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              MISSION BRIEFING
            </span>
            <h2 
              className="text-white text-2xl md:text-3xl font-bold"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              YOUR FIRST 48 HOURS
            </h2>
          </div>
          
          <div className="space-y-4">
            <MissionCard
              number={1}
              title="SUBMIT YOUR CATALOG"
              description="Send us your best 5-10 tracks. Include WAV files, instrumentals, stems, and complete metadata. This is your first impression—make it count."
              action="SUBMIT CATALOG"
              actionLink="mailto:leaver@itzgreenmusic.com?subject=ROSTER%20MEMBER%20-%20First%20Catalog%20Submission"
              icon={FileMusic}
              isPrimary={true}
            />
            
            <MissionCard
              number={2}
              title="CONNECT YOUR PRO"
              description="If you're registered with BMI or ASCAP, send us your IPI number so we can link your publishing administration."
              action="SEND IPI NUMBER"
              actionLink="mailto:leaver@itzgreenmusic.com?subject=IPI%20Number%20-%20PRO%20Connection"
              icon={Zap}
              isPrimary={false}
            />
            
            <MissionCard
              number={3}
              title="FLEX YOUR STATUS"
              description="Download your 'One-Stop Certified' badge and add it to your Instagram bio, Linktree, or BeatStars profile. Let the world know you're pre-cleared."
              action="DOWNLOAD BADGE"
              actionLink="/assets/Certified_One_Stop_Badge.svg"
              icon={Instagram}
              isPrimary={false}
            />
          </div>
        </motion.div>

        {/* ═══ DIRECT LINE ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 text-center"
        >
          <h3 
            className="text-white text-xl font-bold mb-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            DIRECT LINE TO A&R
          </h3>
          <p 
            className="text-[rgb(161,161,170)] text-sm mb-6"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Questions? Concerns? Bangers you need to send immediately? Hit this line.
          </p>
          
          <a
            href={`mailto:${contactInfo.email}`}
            className="inline-flex items-center gap-2 text-[rgb(218,255,1)] text-sm font-semibold tracking-[0.1em] hover:underline"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <Mail size={16} />
            {contactInfo.email}
          </a>
          
          <div className="mt-6 pt-6 border-t border-[rgba(255,255,255,0.06)]">
            <p 
              className="text-[rgb(161,161,170)] text-xs"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Managed by <span className="text-white font-medium">{contactInfo.manager}</span> • {contactInfo.location}
            </p>
          </div>
        </motion.div>

        {/* ═══ FOOTER TAGLINE ═══ */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="text-center mt-12"
        >
          <p 
            className="text-[rgb(218,255,1)] text-sm font-bold tracking-[0.15em]"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            {siteConfig.tagline}
          </p>
          <p 
            className="text-[rgb(161,161,170)] text-xs mt-2"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {siteConfig.copyright}
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default WelcomePage;
